import { useState } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  Select,
  SkeletonText,
  Switch,
} from "@/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useCurrentConfig, useUpdateConfig } from "@/lib/queries";
import { toConfigInput } from "@/lib/utils/config";
import {
  FEATURE_FLAG_KEYS,
  FEATURE_FLAG_LABELS,
  OLIVIA_ACCESS_MODES,
  type FeatureFlags,
  type OliviaAccessMode,
} from "@/lib/api";

const OLIVIA_ACCESS_LABELS: Record<OliviaAccessMode, string> = {
  disabled: "Deshabilitado - sin acceso",
  public: "Publico - acceso general",
  members: "Miembros - acceso restringido",
};

export function FeatureFlagsPage() {
  const configQuery = useCurrentConfig();
  const updateConfig = useUpdateConfig();
  const { toast } = useToast();
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());

  const config = configQuery.data;

  if (configQuery.isLoading) {
    return (
      <div>
        <PageHeader title="Funciones" />
        <Card>
          <CardBody>
            <SkeletonText lines={6} />
          </CardBody>
        </Card>
      </div>
    );
  }

  if (configQuery.isError || !config) {
    return (
      <div>
        <PageHeader title="Funciones" />
        <Card>
          <CardBody>
            <ErrorState
              description="No se pudo cargar la configuracion de funciones."
              onRetry={() => configQuery.refetch()}
            />
          </CardBody>
        </Card>
      </div>
    );
  }

  async function withPending(key: string, action: () => Promise<void>) {
    setPendingKeys((prev) => new Set(prev).add(key));
    try {
      await action();
    } catch {
      toast({ title: "No se pudo guardar el cambio", variant: "error" });
    } finally {
      setPendingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }

  function toggleFeature(key: keyof FeatureFlags, value: boolean) {
    void withPending(key, async () => {
      await updateConfig.mutateAsync({
        ...toConfigInput(config!),
        features: { ...config!.features, [key]: value },
        changeReason: `Actualizar funcion ${key}`,
      });
      toast({
        title: `${FEATURE_FLAG_LABELS[key]} ${value ? "activada" : "desactivada"}`,
        variant: "success",
      });
    });
  }

  function toggleOlivia(enabled: boolean) {
    void withPending("olivia.enabled", async () => {
      await updateConfig.mutateAsync({
        ...toConfigInput(config!),
        olivia: { ...config!.olivia, enabled },
        changeReason: "Actualizar disponibilidad de Olivia",
      });
      toast({ title: `Olivia ${enabled ? "habilitada" : "deshabilitada"}`, variant: "success" });
    });
  }

  function changeOliviaAccessMode(accessMode: OliviaAccessMode) {
    void withPending("olivia.accessMode", async () => {
      await updateConfig.mutateAsync({
        ...toConfigInput(config!),
        olivia: { ...config!.olivia, accessMode },
        changeReason: "Actualizar modo de acceso de Olivia",
      });
      toast({ title: "Modo de acceso de Olivia actualizado", variant: "success" });
    });
  }

  return (
    <div>
      <PageHeader
        title="Funciones"
        description="Activa o desactiva funciones de IESAPP sin publicar una nueva version."
      />

      <div className="space-y-4">
        <Card>
          <CardHeader title="Funciones disponibles" />
          <CardBody className="divide-y divide-ink/5">
            {FEATURE_FLAG_KEYS.map((key) => (
              <div key={key} className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
                <div>
                  <p className="text-sm font-medium text-ink">{FEATURE_FLAG_LABELS[key]}</p>
                  <p className="text-xs text-ink-faint">{key}</p>
                </div>
                <Switch
                  checked={config.features[key]}
                  disabled={pendingKeys.has(key)}
                  onCheckedChange={(value) => toggleFeature(key, value)}
                  aria-label={FEATURE_FLAG_LABELS[key]}
                />
              </div>
            ))}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Olivia" description="Asistente conversacional y su nivel de acceso." />
          <CardBody className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-ink">Olivia habilitada</p>
                <p className="text-xs text-ink-faint">Controla si el punto de entrada aparece en IESAPP.</p>
              </div>
              <Switch
                checked={config.olivia.enabled}
                disabled={pendingKeys.has("olivia.enabled")}
                onCheckedChange={toggleOlivia}
                aria-label="Olivia habilitada"
              />
            </div>

            <div className="max-w-xs space-y-1.5">
              <label htmlFor="olivia-access-mode" className="block text-sm font-medium text-ink">
                Modo de acceso
              </label>
              <Select
                id="olivia-access-mode"
                value={config.olivia.accessMode}
                disabled={pendingKeys.has("olivia.accessMode")}
                onChange={(e) => changeOliviaAccessMode(e.target.value as OliviaAccessMode)}
              >
                {OLIVIA_ACCESS_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {OLIVIA_ACCESS_LABELS[mode]}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-ink-faint">
                El modo &ldquo;miembros&rdquo; queda reservado para un control de acceso propio de miembros,
                independiente de esta consola.
              </p>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
